import { useCallback, useEffect, useMemo, useRef } from 'react'
import { WppButton, WppGrid } from '@wppopen/components-library-react'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import HeaderCell from './header-cell'
import { LoadingOverlay, initialisationColDef, initialisationRowData } from './utils'
import '@wppopen/components-library/dist/collection/ag-theme-wpp.css'
import 'ag-grid-community/styles/ag-grid.css'
import styles from './table.module.scss'
import type { ColDef, SortChangedEvent } from 'ag-grid-community'
import type { SortingDetails } from './table-types.d'
import LoadMoreButton from 'components/Loading/LoadMoreButton'
import clsx from 'clsx'

interface TableProps extends AgGridReactProps {
  /** The number of rows that have been loaded */
  loadedRows: number
  /** The total number of rows that are available from backend */
  totalRows: number
  /** Loading status when the API is being loaded */
  loading?: boolean
  /** Callback on sorting update */
  onSortingUpdate?: (sort: SortingDetails | undefined, element: AgGridReact) => void
  /** Callback on load more */
  onLoadMore?: () => void
  /** Can the user drill through on rows */
  isDrillable?: boolean
  /** table theme */
  theme?: 'primary' | 'secondary' | 'tertiary'
  /** hide full header */
  isHeaderHidden?: boolean
  // if initialising, a skeleton loader will be shown
  isInitialising?: boolean
  // if there is pop up outside table boundary
  isOutsideTableBoundary?: boolean
}

/**
 * A component to create a simple table.
 */
function Table({
  loadedRows,
  totalRows,
  loading = false,
  onSortingUpdate,
  onLoadMore,
  isDrillable = false,
  isHeaderHidden = false,
  theme = 'primary',
  isInitialising = false,
  rowData,
  columnDefs,
  isOutsideTableBoundary,
  ...props
}: TableProps) {
  const gridRef = useRef<AgGridReact>(null)
  const loadingValue = useMemo(() => (loadedRows / totalRows) * 100, [loadedRows, totalRows])

  // handle the sorting and return the sorting details and pass it to onSortingUpdate
  const handleSortingDetails = useCallback(
    (params: SortChangedEvent) => {
      if (!onSortingUpdate || !gridRef?.current) return

      const columns = params.columnApi.getColumnState()
      let sortColumn
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].sort !== null) {
          sortColumn = columns[i]
          break
        }
      }
      if (!sortColumn) return onSortingUpdate(undefined, gridRef.current)

      onSortingUpdate(
        {
          column: sortColumn.colId,
          sort: sortColumn.sort,
        },
        gridRef.current,
      )
    },
    [onSortingUpdate],
  )

  // controller for loading overlay status
  useEffect(() => {
    const table = gridRef?.current
    if (!table || !table.api) return

    if (loading) {
      table.api.showLoadingOverlay()
      return
    } else if (!loading && !rowData?.length) {
      // if there is no data and the loading is done show the no rows overlay
      table.api.showNoRowsOverlay()
      return
    } else if (!loading && !!rowData?.length) {
      // if there is data and the loading is done hide the overlay
      table.api.hideOverlay()
      return
    }
  }, [loading, gridRef, rowData])

  return (
    <div
      className={clsx(styles[`table_${theme}`], {
        [styles.isDrillable]: !isInitialising && isDrillable,
        [styles.no_header]: isHeaderHidden,
        [styles.outsideBoundary]: isOutsideTableBoundary,
      })}
      data-testid="ag-grid-table"
    >
      <AgGridReact
        className="ag-theme-quartz"
        ref={gridRef}
        domLayout="autoHeight"
        rowHeight={48}
        headerHeight={48}
        defaultColDef={{
          headerComponent: HeaderCell,
        }}
        onSortChanged={handleSortingDetails}
        suppressPaginationPanel
        loadingOverlayComponent={LoadingOverlay}
        suppressLoadingOverlay={isInitialising}
        columnDefs={isInitialising ? initialisationColDef(columnDefs as ColDef<any>[]) : columnDefs}
        rowData={isInitialising ? initialisationRowData(columnDefs as ColDef<any>[]) : rowData}
        {...props}
      />

      {loadedRows < totalRows && (
        <WppGrid container direction="column" alignItems="center" justifyContent="center">
          <LoadMoreButton
            value={loadingValue}
            labels={`${loadedRows} of ${totalRows} items`}
            variant="bar"
            className={styles.loading}
          />
          <WppButton variant="secondary" onClick={onLoadMore} data-testid="table-load-more">
            Load more
          </WppButton>
        </WppGrid>
      )}
    </div>
  )
}

export default Table
